import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  Tooltip,
  Typography,
  MenuItem
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { eventTicketsAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface IssueTicketDialogProps {
  open: boolean;
  onClose: () => void;
  productionId: string;
  venueId: string;
  events: any[];
  ticketTypes: any[];
}

const IssueTicketDialog: React.FC<IssueTicketDialogProps> = ({
  open,
  onClose,
  productionId,
  venueId,
  events,
  ticketTypes
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passUrl, setPassUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [formData, setFormData] = useState({
    eventId: '',
    ticketTypeId: '',
    ticketNumber: '',
    orderNumber: '',
    seatSection: '',
    seatRow: '',
    seatNumber: '',
    firstName: '',
    lastName: '',
    email: ''
  });

  const handleClose = () => {
    setFormData({
      eventId: '',
      ticketTypeId: '',
      ticketNumber: '',
      orderNumber: '',
      seatSection: '',
      seatRow: '',
      seatNumber: '',
      firstName: '',
      lastName: '',
      email: ''
    });
    setError(null);
    setSuccess(false);
    setPassUrl(null);
    setCopied(false);
    onClose();
  };

  const handleCopyUrl = () => {
    if (passUrl) {
      navigator.clipboard.writeText(passUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setLoading(true);

    try {
      // Build ticket data
      const ticketData: any = {
        ticketTypeId: formData.ticketTypeId
      };

      // Add event (or let PassKit auto-create it)
      if (formData.eventId) {
        ticketData.eventId = formData.eventId;
      } else {
        // Auto-create event
        const firstEvent = events[0];
        if (firstEvent) {
          ticketData.event = {
            productionId: productionId,
            venueId: venueId,
            scheduledStartDate: firstEvent.scheduledStartDate
          };
        }
      }

      // Add optional fields
      if (formData.ticketNumber) {
        ticketData.ticketNumber = formData.ticketNumber;
      }
      if (formData.orderNumber) {
        ticketData.orderNumber = formData.orderNumber;
      }

      // Add seat info if provided
      if (formData.seatSection || formData.seatRow || formData.seatNumber) {
        ticketData.seatInfo = {
          section: formData.seatSection || undefined,
          row: formData.seatRow || undefined,
          seat: formData.seatNumber || undefined
        };
      }

      // Add person info if provided
      if (formData.firstName || formData.lastName || formData.email) {
        ticketData.person = {
          forename: formData.firstName || undefined,
          surname: formData.lastName || undefined,
          emailAddress: formData.email || undefined
        };
      }

      const response = await eventTicketsAPI.issueTicket(ticketData);

      if (response.success && response.pass_url) {
        setSuccess(true);
        setPassUrl(response.pass_url);
      } else {
        setError('Karta je kreirana, ali pass URL nije vraćen.');
      }
    } catch (err: any) {
      logger.error('Error issuing ticket:', err);
      setError(err.response?.data?.detail || 'Greška pri kreiranju karte.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.ticketTypeId && (formData.eventId || events.length > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Kreiraj Novu Kartu</DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && passUrl ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Karta uspešno kreirana!
            </Alert>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Pass URL"
                value={passUrl}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title={copied ? 'Kopirano!' : 'Kopiraj'}>
                      <IconButton onClick={handleCopyUrl} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  )
                }}
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              Kliknite na link da biste dodali kartu u Apple ili Google Wallet:
            </Typography>
            <Link href={passUrl} target="_blank" rel="noopener noreferrer">
              {passUrl}
            </Link>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Event Selection */}
            <TextField
              select
              label="Događaj"
              value={formData.eventId}
              onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
              helperText="Izaberite postojeći događaj ili ostavite prazno za auto-kreiranje"
            >
              <MenuItem value="">Auto-kreiraj novi događaj</MenuItem>
              {events.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.name} - {event.event_date ? new Date(event.event_date).toLocaleString('sr-RS', { dateStyle: 'short', timeStyle: 'short' }) : 'Nepoznat datum'}
                </MenuItem>
              ))}
            </TextField>

            {/* Ticket Type Selection */}
            <TextField
              select
              required
              label="Tip Karte"
              value={formData.ticketTypeId}
              onChange={(e) => setFormData({ ...formData, ticketTypeId: e.target.value })}
            >
              <MenuItem value="">Izaberite tip karte</MenuItem>
              {ticketTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Ticket Details */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Broj Karte"
                value={formData.ticketNumber}
                onChange={(e) => setFormData({ ...formData, ticketNumber: e.target.value })}
                fullWidth
                helperText="Opciono"
              />
              <TextField
                label="Broj Narudžbine"
                value={formData.orderNumber}
                onChange={(e) => setFormData({ ...formData, orderNumber: e.target.value })}
                fullWidth
                helperText="Opciono"
              />
            </Box>

            {/* Seat Info */}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Sedište (opciono)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Sektor"
                value={formData.seatSection}
                onChange={(e) => setFormData({ ...formData, seatSection: e.target.value })}
                fullWidth
              />
              <TextField
                label="Red"
                value={formData.seatRow}
                onChange={(e) => setFormData({ ...formData, seatRow: e.target.value })}
                fullWidth
              />
              <TextField
                label="Broj Sedišta"
                value={formData.seatNumber}
                onChange={(e) => setFormData({ ...formData, seatNumber: e.target.value })}
                fullWidth
              />
            </Box>

            {/* Person Info */}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Informacije o Korisniku (opciono)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Ime"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                fullWidth
              />
              <TextField
                label="Prezime"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                fullWidth
              />
            </Box>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {success ? 'Zatvori' : 'Otkaži'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!isFormValid || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Kreiraj Kartu
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IssueTicketDialog;
